import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sequencesApi } from '@/lib/api';

export function useSequences(params?: { status?: string }) {
  return useQuery({
    queryKey: ['sequences', params],
    queryFn: () => sequencesApi.list(params),
  });
}

export function useSequence(id: string) {
  return useQuery({
    queryKey: ['sequences', id],
    queryFn: () => sequencesApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: sequencesApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useUpdateSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string; bccEnabled?: boolean; bccEmails?: string[] } }) =>
      sequencesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useDeleteSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sequencesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useApproveSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sequencesApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function usePauseSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sequencesApi.pause(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useResumeSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sequencesApi.resume(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useArchiveSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sequencesApi.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

// Steps
export function useAddStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, data }: {
      sequenceId: string;
      data: { position: number; subject: string; body: string; delayDays?: number; delayHours?: number; replyTo?: boolean };
    }) => sequencesApi.addStep(sequenceId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useUpdateStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, data }: {
      stepId: string;
      data: { subject?: string; body?: string; delayDays?: number; delayHours?: number; replyTo?: boolean };
    }) => sequencesApi.updateStep(stepId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useRemoveStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stepId: string) => sequencesApi.removeStep(stepId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

// Test email
export function useSendTestEmail() {
  return useMutation({
    mutationFn: ({ stepId, recipientEmail, testVariables, variantId }: { stepId: string; recipientEmail: string; testVariables?: Record<string, string>; variantId?: string }) =>
      sequencesApi.sendTestEmail(stepId, recipientEmail, testVariables, variantId),
  });
}

// Variants
export function useAddVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, data }: { stepId: string; data: { subject: string; body: string; weight?: number } }) =>
      sequencesApi.addVariant(stepId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useUpdateVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ variantId, data }: { variantId: string; data: { subject?: string; body?: string; weight?: number } }) =>
      sequencesApi.updateVariant(variantId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useRemoveVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) => sequencesApi.removeVariant(variantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useVariantStats(stepId: string) {
  return useQuery({
    queryKey: ['variant-stats', stepId],
    queryFn: () => sequencesApi.getVariantStats(stepId),
    enabled: !!stepId,
  });
}

// Enrollment → Deal
export function useCreateDealFromEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ enrollmentId, data }: { enrollmentId: string; data: { title: string; value?: number } }) =>
      sequencesApi.createDealFromEnrollment(enrollmentId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

// Enrollment
export function useEnrollContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { contactIds?: string[]; newContacts?: Array<{ email: string; firstName: string; lastName: string; jobTitle?: string }>; dealId?: string; variables?: Record<string, string> } }) =>
      sequencesApi.enroll(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useUpdateEnrollmentVariables() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ enrollmentId, variables }: { enrollmentId: string; variables: Record<string, string> }) =>
      sequencesApi.updateEnrollmentVariables(enrollmentId, variables),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useSendOneNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (executionId: string) => sequencesApi.sendOneNow(executionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useUnenrollContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contactId }: { id: string; contactId: string }) =>
      sequencesApi.unenroll(id, contactId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useSequenceEnrollments(id: string) {
  return useQuery({
    queryKey: ['sequences', id, 'enrollments'],
    queryFn: () => sequencesApi.getEnrollments(id),
    enabled: !!id,
  });
}

export function useSequenceStats(id: string) {
  return useQuery({
    queryKey: ['sequences', id, 'stats'],
    queryFn: () => sequencesApi.getStats(id),
    enabled: !!id,
  });
}

// Scheduling Settings
export function useSequenceSettings() {
  return useQuery({
    queryKey: ['sequence-settings'],
    queryFn: () => sequencesApi.getSettings(),
  });
}

export function useUpdateSequenceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof sequencesApi.updateSettings>[0]) =>
      sequencesApi.updateSettings(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequence-settings'] });
    },
  });
}

// Gmail
export function useGmailStatus() {
  return useQuery({
    queryKey: ['gmail-status'],
    queryFn: () => sequencesApi.getGmailStatus(),
  });
}

export function useGmailAuthUrl() {
  return useMutation({
    mutationFn: () => sequencesApi.getGmailAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export function useGmailDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => sequencesApi.disconnectGmail(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gmail-status'] });
    },
  });
}
